import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from '@mui/material';

import { StyledTextField } from '@labradorsports/components';
import { useSite } from '@labradorsports/utils';

import { useDispatcher } from '../../shared/hooks/index.js';
import { addValidation, useValidation } from '../../shared/providers/index.js';
import { HelmetWrapper } from '../../shared/structural/index.js';
import { Selectors, useLoginMutation, LoginPopoverForms, authActions } from '../../store/index.js';

interface Props {
    changeForm?: (form?: string) => any;
}

const LoginForm: FC<Props> = ({ changeForm }) => {
    const { Config } = useSite();
    const [login] = useLoginMutation();
    const setLoginEmail = useDispatcher(authActions.SetLoginEmail);
    const accountFormError = useSelector(Selectors.accountFormError);
    const email = useSelector(Selectors.loginEmail);
    const [password, setPassword] = useState('');

    const { validateRef, errors, submitted, setSubmitted } = useValidation(() => {
        const newError: any = {};

        if (email === '') {
            newError.email = true;
        }

        if (password === '') {
            newError.password = true;
        }

        return newError;
    }, [email, password]);

    const confirmLogin = async (evt?) => {
        evt?.preventDefault();

        setSubmitted(false);
        if (await validateRef.current()) {
            login({ email, password });
        } else {
            setSubmitted(true);
        }
    };

    const emailChange = (event: any) => {
        setLoginEmail(event.target.value);
    };

    const passwordChange = (event: any) => {
        setPassword(event.target.value);
    };

    const showResetPassword = () => {
        changeForm(LoginPopoverForms.FORGOT_PASSWORD);
    };

    const canonical = `${Config.SiteConfig.Url}/login`;

    return (
        <div className="login-form">
            <HelmetWrapper title="Login">
                <link rel="canonical" href={canonical} />
                <meta property="og:url" content={canonical} />
            </HelmetWrapper>
            <form onSubmit={confirmLogin} autoComplete="on">
                <StyledTextField
                    value={email}
                    onChange={emailChange}
                    label="Email"
                    name="email"
                    slotProps={{
                        input: {
                            autoComplete: 'on',
                        },
                    }}
                    helperText={submitted && errors.email && 'Email is required'}
                    error={submitted && errors.email}
                    fullWidth
                />
                <StyledTextField
                    type="password"
                    value={password}
                    onChange={passwordChange}
                    label="Password"
                    name="password"
                    slotProps={{
                        input: {
                            autoComplete: 'current-password',
                        },
                    }}
                    helperText={submitted && errors.password && 'Password is required'}
                    error={submitted && errors.password}
                    fullWidth
                />
                {accountFormError ? <span className="error-text">{accountFormError.message}</span> : undefined}
                <div>
                    <div className="small">Trouble logging in?</div>
                    <Button tabIndex={1} onClick={showResetPassword}>
                        Reset Password
                    </Button>
                </div>
                <Button variant="contained" color="secondary" type="submit" fullWidth>
                    Login
                </Button>
            </form>
        </div>
    );
};

export default addValidation(LoginForm);
